<template>
  <main class="preview-page">
    <NativeCloudPhonePlayer
      v-if="deviceLoaded && device.deviceStatus === 'ONLINE'"
      ref="player"
      :device-id="deviceId"
      mode="preview"
      :show-controls="false"
      @route-opened="reportPreviewRoute"
      @status-change="reportPreviewStatus"
    />

    <div v-else-if="deviceLoaded" class="preview-unavailable">Preview unavailable</div>
  </main>
</template>

<script setup>
/* global defineProps */
import { onBeforeUnmount, onMounted, ref } from 'vue'
import NativeCloudPhonePlayer from '../components/cloudphone/NativeCloudPhonePlayer.vue'
import { fetchCloudDevice } from '../api/cloudDevice'

const props = defineProps({
  deviceId: {
    type: [Number, String],
    required: true
  }
})

const device = ref({})
const deviceLoaded = ref(false)
const player = ref(null)

async function loadDevice() {
  try {
    device.value = await fetchCloudDevice(props.deviceId)
  } catch (error) {
    reportPreviewError(error)
  } finally {
    deviceLoaded.value = true
  }
}

function reportPreviewStatus({ status }) {
  if (window.parent === window) return
  window.parent.postMessage({
    type: 'cloudphone-native-preview-status',
    deviceId: String(props.deviceId),
    status
  }, window.location.origin)
}

function reportPreviewRoute(route) {
  if (window.parent === window || !route?.routeId) {
    return
  }

  window.parent.postMessage({
    type: 'cloudphone-native-preview-route-opened',
    deviceId: String(props.deviceId),
    routeId: route.routeId
  }, window.location.origin)
}

onMounted(loadDevice)

async function handleParentMessage(event) {
  if (event.origin !== window.location.origin || event.source !== window.parent) {
    return
  }

  const message = event.data
  if (message?.type !== 'cloudphone-native-close-preview'
    || message.deviceId !== String(props.deviceId)) {
    return
  }

  try {
    await player.value?.closeProjection()
  } finally {
    window.parent.postMessage({
      type: 'cloudphone-native-preview-closed',
      deviceId: String(props.deviceId),
      requestId: message.requestId
    }, window.location.origin)
  }
}

function previewErrorMessage(error) {
  if (typeof error === 'string') {
    return error
  }

  if (error?.message) {
    return error.message
  }

  return String(error || 'Unknown preview error')
}

function reportPreviewError(error) {
  if (window.parent === window) {
    return
  }

  window.parent.postMessage({
    type: 'cloudphone-native-preview-error',
    deviceId: String(props.deviceId),
    message: previewErrorMessage(error)
  }, window.location.origin)
}

function handlePreviewWindowError(event) {
  reportPreviewError(event.error || event.message)
}

function handlePreviewUnhandledRejection(event) {
  reportPreviewError(event.reason)
}

onMounted(() => {
  window.addEventListener('message', handleParentMessage)
  window.addEventListener('error', handlePreviewWindowError)
  window.addEventListener('unhandledrejection', handlePreviewUnhandledRejection)
})

onBeforeUnmount(() => {
  window.removeEventListener('message', handleParentMessage)
  window.removeEventListener('error', handlePreviewWindowError)
  window.removeEventListener('unhandledrejection', handlePreviewUnhandledRejection)
})
</script>

<style scoped>
.preview-page {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #090e13;
}

.preview-unavailable {
  display: grid;
  width: 100%;
  height: 100%;
  color: #b5c1c6;
  font-size: 12px;
  place-items: center;
}
</style>
